import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, Plus, Share2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { describeFailure } from '@/lib/i18n/errors';
import { useToast } from '@/components/ui/toast';
import { InlineTitle } from '@/components/ui/inline-title';
import { LoadingState } from '@/components/ui/states';
import { useKbMutation, useKbNotes, useKbSecrets, useKbTopics } from '@/hooks/useKb';
import { KbMembershipBar, KbShareDialog } from '@/routes/kb/KbShare';
import { kb } from '@/lib/api/endpoints';
import type { KbSecretRevealed,
  KbSecretTemplate,
} from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Page } from '@/routes/Today';

/**
 * One topic: its notes, newest edits first, and its secrets underneath.
 *
 * Notes and secrets live side by side on purpose — "the FTP guide" and "the
 * FTP password" belong on the same shelf, which is the whole reason the vault
 * is in the knowledge base rather than a separate app.
 */
export function KbTopicRoute() {
  const t = useT();
  const navigate = useNavigate();
  const { topicId = '' } = useParams();
  const { data: topics } = useKbTopics();
  const { isPending } = useKbNotes(topicId);

  const topic = (topics ?? []).find((entry) => entry.id === topicId);
  const [draft, setDraft] = useState('');
  const [sharing, setSharing] = useState(false);
  const isOwner = (topic?.my_role ?? 'OWNER') === 'OWNER';
  const renameTopic = useKbMutation((name: string) => kb.renameTopic(topicId, name));
  const canEdit = isOwner || topic?.my_role === 'EDITOR';

  const createNote = useKbMutation(kb.createNote, (note) => {
    setDraft('');
    navigate(`/kb/notes/${note.id}`);
  });
  const deleteTopic = useKbMutation(kb.deleteTopic, () => navigate('/today'));

  if (isPending) {
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  }

  return (
    <Page>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-content">
            {topic ? (
              <InlineTitle
                value={topic.name}
                disabled={!isOwner}
                onSave={(name) => renameTopic.mutate(name)}
                ariaLabel={t('kb.renameTopic')}
                inputClassName="text-3xl"
              />
            ) : (
              t('kb.title')
            )}
          </h1>
          <p className="mt-1 text-sm text-content-subtle">{t('kb.topicHint')}</p>
        </div>
        {isOwner ? (
          <div className="flex items-center gap-1.5">
            <Button variant="secondary" size="sm" onClick={() => setSharing(true)}>
              <Share2 className="h-3.5 w-3.5" aria-hidden />
              {t('kb.share')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm(t('kb.deleteTopicConfirm')))
                  deleteTopic.mutate(topicId);
              }}
              className="text-content-subtle hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </div>
        ) : null}
      </header>

      {topic ? (
        <KbMembershipBar topic={topic} onLeft={() => navigate('/today')} />
      ) : null}

      {isOwner ? (
        <KbShareDialog
          topicId={topicId}
          open={sharing}
          onClose={() => setSharing(false)}
        />
      ) : null}

      {/* Capture first — the two-second promise applies to notes too. */}
      {canEdit ? (
        <form
          className="mb-6 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const title = draft.trim();
            if (title) createNote.mutate({ topic_id: topicId, title });
          }}
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('kb.newNote')}
            className={cn(
              'h-10 flex-1 rounded-xl bg-surface px-4 text-sm shadow-xs ring-1',
              'ring-border/60 outline-none placeholder:text-content-subtle',
              'focus:ring-accent/50',
            )}
          />
          <Button type="submit" variant="primary" disabled={!draft.trim()}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('common.add')}
          </Button>
        </form>
      ) : null}

      <SecretsSection topicId={topicId} canEdit={canEdit} />
    </Page>
  );
}

/** The vault. Renders nothing at all when the server has no encryption key. */
function SecretsSection({ topicId, canEdit }: { topicId: string; canEdit: boolean }) {
  const t = useT();
  const { data: secrets, isError } = useKbSecrets(topicId);
  const [adding, setAdding] = useState(false);
  const [revealed, setRevealed] = useState<KbSecretRevealed | null>(null);

  const create = useKbMutation(kb.createSecret, () => setAdding(false));
  const reveal = useKbMutation(kb.revealSecret, setRevealed);
  const remove = useKbMutation(kb.deleteSecret, () => setRevealed(null));

  if (isError) return null; // no vault on this server

  return (
    <section className="mt-10 border-t border-border pt-6">
      <div className="mb-3 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-content-subtle" aria-hidden />
        <h2 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
          {t('kb.secrets')}
        </h2>
        <span className="flex-1" />
        {canEdit ? (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {t('kb.newSecret')}
          </Button>
        ) : null}
      </div>
      <p className="mb-3 text-xs text-content-subtle">{t('kb.secretsHint')}</p>

      {adding ? (
        <SecretForm
          onCancel={() => setAdding(false)}
          onSave={(name, fields) => create.mutate({ topic_id: topicId, name, fields })}
        />
      ) : null}

      <ul className="space-y-1.5">
        {(secrets ?? []).map((secret) => (
          <li
            key={secret.id}
            className="rounded-xl bg-surface px-4 py-2.5 shadow-xs ring-1 ring-border/60"
          >
            <div className="flex items-center gap-3">
              <Lock className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-sm text-content">
                {secret.name}
              </span>
              {revealed?.id === secret.id ? (
                <Button variant="ghost" size="sm" onClick={() => setRevealed(null)}>
                  {t('kb.hide')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => reveal.mutate(secret.id)}
                >
                  {t('kb.reveal')}
                </Button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('kb.deleteSecretConfirm')))
                    remove.mutate(secret.id);
                }}
                aria-label={t('common.delete')}
                className="rounded p-1 text-content-subtle hover:text-danger"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>

            {revealed?.id === secret.id ? (
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t border-border pt-2">
                {Object.entries(revealed.fields).map(([field, value]) => (
                  <div key={field} className="contents">
                    <dt className="text-xs text-content-subtle">{field}</dt>
                    <dd>
                      <code className="select-all break-all text-xs text-content">
                        {value}
                      </code>
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Free-form fields: host, user, password, whatever the credential needs. */
function SecretForm({
  onCancel,
  onSave,
}: {
  onCancel: () => void;
  onSave: (name: string, fields: Record<string, string>) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [rows, setRows] = useState<{ key: string; value: string }[]>([
    { key: 'host', value: '' },
    { key: 'user', value: '' },
    { key: 'password', value: '' },
  ]);
  const [template, setTemplate] = useState<string>('');
  const [managing, setManaging] = useState(false);
  const { data: templates } = useQuery({
    queryKey: ['kb', 'secret-templates'],
    queryFn: () => kb.secretTemplates(),
  });

  // A template is the list of field names, applied by pre-filling the rows.
  // Values already typed into rows that survive are kept — picking a template
  // after starting to type must not throw the typing away.
  const applyTemplate = (id: string) => {
    setTemplate(id);
    const chosen = templates?.find((row) => row.id === id);
    if (!chosen) return;
    setRows(
      chosen.fields.map((key) => ({
        key,
        value: rows.find((row) => row.key === key)?.value ?? '',
      })),
    );
  };

  return (
    <form
      className="mb-3 space-y-2 rounded-xl bg-surface p-3 shadow-xs ring-1 ring-accent/40"
      onSubmit={(event) => {
        event.preventDefault();
        const fields = Object.fromEntries(
          rows
            .filter((row) => row.key.trim() && row.value)
            .map((row) => [row.key, row.value]),
        );
        if (name.trim() && Object.keys(fields).length > 0) onSave(name.trim(), fields);
      }}
    >
      {/* Which kind of access this is. Built-ins first, then your own; the
          last entry opens the manager for adding or editing your own. */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={template}
          onChange={(event) => {
            if (event.target.value === '__manage__') {
              setManaging(true);
              return;
            }
            applyTemplate(event.target.value);
          }}
          aria-label={t('kb.template')}
          className="h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-2 text-sm text-content outline-none focus:border-accent max-sm:h-11 touch:h-11"
        >
          <option value="">{t('kb.templatePick')}</option>
          {(templates ?? [])
            .filter((row) => row.builtin)
            .map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          {(templates ?? []).some((row) => !row.builtin) ? (
            <optgroup label={t('kb.templateMine')}>
              {(templates ?? [])
                .filter((row) => !row.builtin)
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
            </optgroup>
          ) : null}
          <option value="__manage__">{t('kb.templateManage')}</option>
        </select>
      </div>
      {managing ? (
        <TemplateManager
          templates={(templates ?? []).filter((row) => !row.builtin)}
          currentFields={rows.map((row) => row.key.trim()).filter(Boolean)}
          onClose={() => setManaging(false)}
        />
      ) : null}
      <input
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={t('kb.secretName')}
        className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-accent max-sm:h-11 touch:h-11"
      />
      {rows.map((row, index) => (
        <div key={index} className="flex gap-2">
          <input
            value={row.key}
            onChange={(event) =>
              setRows(
                rows.map((r, i) =>
                  i === index ? { ...r, key: event.target.value } : r,
                ),
              )
            }
            placeholder={t('kb.fieldName')}
            className="h-8 w-32 rounded-md border border-border bg-surface px-2 text-xs outline-none"
          />
          <input
            value={row.value}
            onChange={(event) =>
              setRows(
                rows.map((r, i) =>
                  i === index ? { ...r, value: event.target.value } : r,
                ),
              )
            }
            placeholder={t('kb.fieldValue')}
            type={row.key.toLowerCase().includes('pass') ? 'password' : 'text'}
            className="h-8 flex-1 rounded-md border border-border bg-surface px-2 text-xs outline-none"
          />
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setRows([...rows, { key: '', value: '' }])}
          className="text-xs text-content-subtle hover:text-content"
        >
          + {t('kb.addField')}
        </button>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" size="sm" type="submit">
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Your own templates: save the fields you are typing right now as one, rename
 * or delete the ones you have. Built-ins are not editable — they are the
 * shared vocabulary, and a "host" that means something else per person is
 * worse than no template.
 */
function TemplateManager({
  templates,
  currentFields,
  onClose,
}: {
  templates: KbSecretTemplate[];
  currentFields: string[];
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['kb', 'secret-templates'] });
  const create = useMutation({
    mutationFn: () => kb.createSecretTemplate({ name: name.trim(), fields: currentFields }),
    onSuccess: () => {
      setName('');
      refresh();
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });
  const rename = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      kb.updateSecretTemplate(input.id, { name: input.name }),
    onSuccess: refresh,
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => kb.deleteSecretTemplate(id),
    onSuccess: refresh,
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  return (
    <div className="space-y-2 rounded-lg border border-border bg-surface-sunken p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-content-subtle">
          {t('kb.templateMine')}
        </p>
        <button type="button" onClick={onClose} className="text-xs text-content-subtle hover:text-content">
          {t('common.close')}
        </button>
      </div>
      {templates.length === 0 ? (
        <p className="text-xs text-content-subtle">{t('kb.templateNone')}</p>
      ) : (
        <ul className="space-y-1">
          {templates.map((row) => (
            <li key={row.id} className="flex items-center gap-2 text-sm">
              <InlineTitle
                value={row.name}
                onSave={(next) => rename.mutate({ id: row.id, name: next })}
                ariaLabel={t('kb.templateRename')}
                className="min-w-0 flex-1 font-normal"
                inputClassName="text-sm font-normal"
              />
              <span className="hidden truncate text-xs text-content-subtle sm:inline">
                {row.fields.join(' · ')}
              </span>
              <button
                type="button"
                onClick={() => remove.mutate(row.id)}
                aria-label={t('common.delete')}
                className="rounded p-1.5 text-content-subtle hover:bg-surface-hover hover:text-danger max-sm:p-2.5 touch:p-2.5"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Save what is in the form right now as a template of its own.
          Not a <form>: this sits inside the secret's own form, and HTML forbids
          nesting them — the inner submit lands on the outer form and saves a
          secret instead. Enter is handled by hand for the same reason. */}
      <div className="flex gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            event.stopPropagation();
            if (name.trim() && currentFields.length > 0) create.mutate();
          }}
          placeholder={t('kb.templateNamePlaceholder', { fields: currentFields.join(', ') || '—' })}
          className="h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-2 text-sm outline-none focus:border-accent max-sm:h-11 touch:h-11"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!name.trim() || currentFields.length === 0}
          onClick={() => create.mutate()}
        >
          {t('kb.templateSave')}
        </Button>
      </div>
    </div>
  );
}
